<template>
  <div class="dashboard">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-overlay>

    <!-- Statistics Cards Row -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3" v-for="stat in stats" :key="stat.title">
        <v-card class="stat-card" elevation="2">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div class="stat-content">
                <div class="stat-title">{{ stat.title }}</div>
                <div class="stat-value">{{ stat.value.toLocaleString() }}</div>
                <div class="stat-change" :class="stat.change > 0 ? 'positive' : 'negative'">
                  <v-icon size="16" :icon="stat.change > 0 ? 'mdi-trending-up' : 'mdi-trending-down'"></v-icon>
                  {{ Math.abs(stat.change) }}% so với tuần trước
                </div>
              </div>
              <div class="stat-icon">
                <v-icon :icon="stat.icon" size="48" :color="stat.color"></v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Chart Row -->
    <v-row>
      <v-col cols="12" class="chart-section">
        <LineChart 
          title="Doanh thu theo ngày" 
          :data="chartData"
        />
      </v-col>
    </v-row>

    <!-- Top Products Table Row -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card elevation="2" class="top-products-card">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-trophy" class="mr-2" color="warning"></v-icon>
            Top 10 sản phẩm bán chạy nhất
          </v-card-title>
          <v-card-text class="pt-2">
            <v-data-table
              :headers="productHeaders"
              :items="topProducts"
              :items-per-page="10"
              class="top-products-table"
              hide-default-footer
              no-data-text="Không có dữ liệu"
            >
              <!-- Product Name with Image -->
              <template v-slot:item.productName="{ item }">
                <div class="product-info">
                  <v-avatar size="40" class="mr-3">
                    <v-img :src="item.image" :alt="item.productName">
                      <template v-slot:placeholder>
                        <v-icon icon="mdi-package" size="20"></v-icon>
                      </template>
                    </v-img>
                  </v-avatar>
                  <div>
                    <div class="product-name">{{ item.productName }}</div>
                    <div class="product-sku">SKU: {{ item.sku }}</div>
                  </div>
                </div>
              </template>

              <!-- Category with Chip -->
              <template v-slot:item.category="{ item }">
                <v-chip
                  :color="item.categoryColor"
                  variant="tonal"
                  size="small"
                >
                  {{ item.category }}
                </v-chip>
              </template>

              <!-- Total Sold with Trend -->
              <template v-slot:item.totalSold="{ item }">
                <div class="d-flex align-center">
                  <span class="font-weight-bold">{{ item.totalSold.toLocaleString() }}</span>
                  <v-chip
                    v-if="item.trend"
                    :color="item.trend > 0 ? 'success' : 'error'"
                    variant="tonal"
                    size="x-small"
                    class="ml-2"
                  >
                    <v-icon 
                      :icon="item.trend > 0 ? 'mdi-trending-up' : 'mdi-trending-down'" 
                      size="12"
                      class="mr-1"
                    ></v-icon>
                    {{ Math.abs(item.trend) }}%
                  </v-chip>
                </div>
              </template>

              <!-- Total Revenue -->
              <template v-slot:item.totalRevenue="{ item }">
                <div class="revenue-cell">
                  <span class="font-weight-bold revenue-amount">
                    {{ item.totalRevenue.toLocaleString() }} ₫
                  </span>
                </div>
              </template>

              <!-- Ranking -->
              <template v-slot:item.rank="{ item }">
                <v-chip
                  :color="getRankColor(item.rank)"
                  variant="elevated"
                  size="small"
                  class="rank-chip"
                >
                  #{{ item.rank }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions Row -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-flash" class="mr-2"></v-icon>
            Thao tác nhanh
          </v-card-title>
          <v-card-text class="pt-2">
            <v-row>
              <v-col cols="12" sm="6" md="3" v-for="action in quickActions" :key="action.title">
                <v-btn 
                  :to="action.to"
                  variant="outlined" 
                  :color="action.color"
                  block
                  size="large"
                  class="quick-action-btn"
                >
                  <v-icon :icon="action.icon" class="mr-2"></v-icon>
                  {{ action.title }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LineChart from '@/components/LineChart.vue'
import axios from '@/http';
export default {
  name: 'Dashboard',
  components: {
    LineChart
  },
  data() {
    return {
      loading: true,
      stats: [
        {
          title: 'Số người dùng',
          value: 0,
          change: 0,
          icon: 'mdi-account-group',
          color: 'primary'
        },
        {
          title: 'Tổng số đơn',
          value: 0,
          change: 0,
          icon: 'mdi-cart',
          color: 'success'
        },
        {
          title: 'Đơn đã thanh toán',
          value: 0,
          change: 0,
          icon: 'mdi-check-circle',
          color: 'info'
        },
        {
          title: 'Tổng tiền đã thanh toán',
          value: 0,
          change: 0,
          icon: 'mdi-currency-usd',
          color: 'warning'
        }
      ],
      chartData: [0, 0, 0, 0, 0, 0, 0],
      productHeaders: [
        { 
          title: '#', 
          key: 'rank', 
          width: '80px',
          align: 'center'
        },
        { 
          title: 'Tên sản phẩm', 
          key: 'productName', 
          width: '300px',
          sortable: false
        },
        { 
          title: 'Danh mục', 
          key: 'category', 
          width: '150px',
          align: 'center'
        },
        { 
          title: 'Tổng bán', 
          key: 'totalSold', 
          width: '150px',
          align: 'center'
        },
        { 
          title: 'Tổng tiền', 
          key: 'totalRevenue', 
          width: '180px',
          align: 'right'
        }
      ],
      topProducts: [],
      quickActions: [
        {
          title: 'Thêm sản phẩm',
          icon: 'mdi-plus',
          color: 'primary',
          to: '/admin/product'
        },
        {
          title: 'Xem đơn hàng',
          icon: 'mdi-clipboard-list',
          color: 'success',
          to: '/admin/order'
        },
        {
          title: 'Quản lý người dùng',
          icon: 'mdi-account-cog',
          color: 'info',
          to: '/admin/user'
        },
        {
          title: 'Tạo mã giảm giá',
          icon: 'mdi-ticket-percent',
          color: 'warning',
          to: '/admin/promo'
        }
      ]
    }
  },
  methods: {
    async getAll() {
      this.loading = true;
      try {
        const response = await axios.get('/admin/dashboard/all');
        console.log(response);
        
        if (response.data.success) {
          const data = response.data.data;
          
          // Update stats cards
          this.stats[0].value = data.totalUsers;
          this.stats[0].change = this.calculateRandomTrend(); // Random trend for demo
          
          this.stats[1].value = data.totalOrders;
          this.stats[1].change = this.calculateRandomTrend();
          
          this.stats[2].value = Math.floor(data.totalOrders * 0.85); // Assume 85% paid orders
          this.stats[2].change = this.calculateRandomTrend();
          
          this.stats[3].value = parseInt(data.totalRevenue) || 0;
          this.stats[3].change = this.calculateRandomTrend();
          
          // Update chart data - revenue by days (reverse to show chronological order)
          this.chartData = data.revenueByDays.reverse().map(item => parseInt(item.revenue) || 0);
          
          // Update top products
          if (data.topSellingProducts && data.topSellingProducts.length > 0) {
            this.topProducts = data.topSellingProducts.map((product, index) => ({
              rank: index + 1,
              productName: product.product_name,
              sku: `SKU${product.id.toString().padStart(3, '0')}`,
              category: product.category_name,
              categoryColor: this.getCategoryColor(product.category_name),
              totalSold: product.total_sold,
              totalRevenue: parseInt(product.total_revenue) || 0,
              trend: product.percentage_change,
              image: `https://via.placeholder.com/80x80/${this.getRandomColor()}/ffffff?text=${product.product_name.substring(0, 3).toUpperCase()}`
            }));
          } else {
            // Keep original sample data if no products returned
            this.topProducts = [];
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    getRankColor(rank) {
      if (rank <= 3) return 'warning'
      if (rank <= 5) return 'primary'
      if (rank <= 7) return 'success'
      return 'grey'
    },
    getCategoryColor(categoryName) {
      const colors = ['primary', 'success', 'info', 'warning', 'error', 'purple', 'pink', 'indigo'];
      const hash = categoryName.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return colors[Math.abs(hash) % colors.length];
    },
    getRandomColor() {
      const colors = ['1976d2', '4caf50', 'ff9800', '2196f3', 'f44336', '9c27b0', 'ff5722', 'e91e63', '607d8b', '795548'];
      return colors[Math.floor(Math.random() * colors.length)];
    },
    calculateRandomTrend() {
      // Generate random trend between -20 and +30 for demo purposes
      return Math.round((Math.random() * 50 - 20) * 10) / 10;
    }
  },
  mounted() {
    this.getAll();
  }
}
</script>

<style scoped>
.dashboard {
  padding: 0;
}

.dashboard-header {
  margin-bottom: 24px;
}

.dashboard-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.dashboard-subtitle {
  font-size: 16px;
  color: #666;
  margin: 4px 0 0 0;
}

.stat-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.stat-content {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-change {
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive {
  color: #4caf50;
}

.stat-change.negative {
  color: #f44336;
}

.stat-icon {
  opacity: 0.8;
  margin-left: 16px;
}

.quick-action-btn {
  height: 56px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}

.quick-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.chart-section {
  padding: 0 !important;
}

/* Top Products Table */
.top-products-card {
  border-radius: 12px;
  border: 1px solid #f0f0f0;
}

.top-products-table {
  border-radius: 8px;
}

.product-info {
  display: flex;
  align-items: center;
  min-width: 250px;
}

.product-name {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 14px;
  line-height: 1.2;
  margin-bottom: 2px;
}

.product-sku {
  font-size: 12px;
  color: #666;
  font-weight: 400;
}

.revenue-cell {
  text-align: right;
}

.revenue-amount {
  color: #4caf50;
  font-size: 14px;
}

.rank-chip {
  font-weight: 600;
  min-width: 40px;
}

/* Table Row Hover Effects */
.top-products-table :deep(.v-data-table__wrapper tbody tr:hover) {
  background-color: #f8f9fa !important;
  cursor: pointer;
}

.top-products-table :deep(.v-data-table-header) {
  background-color: #fafafa;
}

.top-products-table :deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: #333 !important;
  font-size: 13px !important;
  border-bottom: 2px solid #e0e0e0 !important;
}

/* Responsive */
@media (max-width: 960px) {
  .dashboard-title {
    font-size: 28px;
  }
  
  .stat-value {
    font-size: 24px;
  }
  
  .stat-icon {
    margin-left: 12px;
  }
}

@media (max-width: 600px) {
  .dashboard-title {
    font-size: 24px;
  }
  
  .stat-value {
    font-size: 20px;
  }
  
  .stat-card .v-card-text {
    padding: 16px !important;
  }
  
  .quick-action-btn {
    height: 48px !important;
  }
  
  /* Table responsive */
  .product-info {
    min-width: 200px;
  }
  
  .product-name {
    font-size: 13px;
  }
  
  .product-sku {
    font-size: 11px;
  }
  
  .top-products-table :deep(.v-data-table-header th) {
    font-size: 12px !important;
    padding: 8px 4px !important;
  }
  
  .top-products-table :deep(.v-data-table__wrapper tbody td) {
    padding: 8px 4px !important;
  }
  
  .revenue-amount {
    font-size: 13px;
  }
}
</style>